import { useState, useCallback, useMemo } from 'react';
import { PRIVACY_ACT_CATEGORIES, PRIVACY_ACT_QUESTIONNAIRE } from '../constants/questions';
import { Answer, CategoryResponse, ComplianceLevel, PrivacyActAssessment } from '../types';

export const useQuestionnaire = (tenantId: string) => {
  // Initialize responses for all categories
  const [responses, setResponses] = useState<Record<string, CategoryResponse>>(() => {
    // Create initial state with null answers for all categories
    const initialResponses: Record<string, CategoryResponse> = {};
    PRIVACY_ACT_CATEGORIES.forEach(category => {
      initialResponses[category.id] = {
        categoryId: category.id,
        answer: null,
        complianceLevel: 0
      };
    });
    return initialResponses;
  });

  // Handle answer changes
  const handleAnswerChange = useCallback((questionId: string, answer: Answer) => {
    // Find the corresponding category
    let categoryId = '';

    PRIVACY_ACT_CATEGORIES.forEach(category => {
      if (category.question.id === questionId) {
        categoryId = category.id;
      }
    });

    if (categoryId) {
      setResponses(prev => {
        // Create a copy of the previous state
        const newResponses = { ...prev };
        const categoryResponse = { ...newResponses[categoryId] };
        
        // Update the answer
        categoryResponse.answer = answer;

        // Calculate compliance level based on answer
        const complianceLevel = calculateComplianceLevel(answer);
        categoryResponse.complianceLevel = complianceLevel;

        // Update the category in the responses object
        newResponses[categoryId] = categoryResponse;
        return newResponses;
      });
    }
  }, []);

  // Calculate compliance level based on answer
  const calculateComplianceLevel = (answer: Answer): ComplianceLevel => {
    switch (answer) {
      case 'Yes':
        return 3; // Fully compliant
      case 'No':
        return 0; // Not compliant
      case 'Unsure':
        return 1; // Partial compliance/needs review
      default:
        return 0; // No answer = not compliant
    }
  };

  // Calculate overall compliance percentages
  const overallCompliance = useMemo(() => {
    const categoryCount = PRIVACY_ACT_CATEGORIES.length;
    let levelZeroCount = 0;
    let levelOneCount = 0;
    let levelTwoCount = 0;
    let levelThreeCount = 0;

    Object.values(responses).forEach(categoryResponse => {
      switch (categoryResponse.complianceLevel) {
        case 0:
          levelZeroCount++;
          break;
        case 1:
          levelOneCount++;
          break;
        case 2:
          levelTwoCount++;
          break;
        case 3:
          levelThreeCount++;
          break;
      }
    });

    return {
      levelZero: Math.round((levelZeroCount / categoryCount) * 100),
      levelOne: Math.round((levelOneCount / categoryCount) * 100),
      levelTwo: Math.round((levelTwoCount / categoryCount) * 100),
      levelThree: Math.round((levelThreeCount / categoryCount) * 100)
    };
  }, [responses]);

  // Get the assessment data
  const assessment: PrivacyActAssessment = useMemo(() => ({
    tenantId,
    timestamp: new Date(),
    responses,
    overallCompliance,
    questionnaireVersion: PRIVACY_ACT_QUESTIONNAIRE.version
  }), [tenantId, responses, overallCompliance]);

  return {
    categories: PRIVACY_ACT_CATEGORIES,
    responses,
    handleAnswerChange,
    assessment
  };
};

export default useQuestionnaire;
